import React, { useEffect, useState } from "react";
import {
  collection, addDoc, getDocs, updateDoc,
  deleteDoc, doc, query, orderBy, serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";

import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TablePagination, InputAdornment, Grid
} from "@mui/material";

import { Plus, Search, Edit3, Trash2 } from "lucide-react";
import Swal from "sweetalert2";

function Affiliatesources() {

  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    id: null,
    platform: "",
    sourceName: "",
    code: "",
    name: "",
    mobile: "",
    address: "",
    status: "Active"
  });

  // 🔹 Auto Generate Code
  const generateCode = () => {
    return "SRC-" + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  // 🔹 Fetch Data
  const fetchData = async () => {
    try {
      const q = query(collection(db, "affiliate_sources"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setData(arr);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 🔹 Save (Add / Update)
  const handleSubmit = async () => {

    if (!formData.platform) {
      return Swal.fire("Error", "Platform required", "error");
    }

    if (!formData.sourceName.trim()) {
      return Swal.fire("Error", "Source name required", "error");
    }

    if (!formData.name.trim()) {
      return Swal.fire("Error", "Customer name required", "error");
    }

    if (!/^[0-9]{10}$/.test(formData.mobile)) {
      return Swal.fire("Error", "Valid 10 digit mobile required", "error");
    }

    try {
      const payload = {
        platform: formData.platform,
        sourceName: formData.sourceName,
        code: formData.code || generateCode(),

        name: formData.name,
        mobile: formData.mobile,
        address: formData.address,

        status: formData.status,
        updatedAt: serverTimestamp()
      };

      if (formData.id) {
        await updateDoc(doc(db, "affiliate_sources", formData.id), payload);
      } else {
        await addDoc(collection(db, "affiliate_sources"), {
          ...payload,
          createdAt: serverTimestamp()
        });
      }

      Swal.fire("Success", "Saved successfully", "success");

      setOpen(false);
      resetForm();
      fetchData();

    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message, "error");
    }
  };

  // 🔹 Delete
  const handleDelete = async (id) => {
    try {
      const confirm = await Swal.fire({
        title: "Delete?",
        text: "This cannot be undone",
        icon: "warning",
        showCancelButton: true
      });

      if (confirm.isConfirmed) {
        await deleteDoc(doc(db, "affiliate_sources", id));
        Swal.fire("Deleted", "Record removed", "success");
        fetchData();
      }
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  // 🔹 Reset
  const resetForm = () => {
    setFormData({
      id: null,
      platform: "",
      sourceName: "",
      code: "",
      name: "",
      mobile: "",
      address: "",
      status: "Active"
    });
  };

  // 🔹 Search
  const filtered = data.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.mobile?.includes(search) ||
    d.platform?.toLowerCase().includes(search.toLowerCase()) ||
    d.sourceName?.toLowerCase().includes(search.toLowerCase()) ||
    d.code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={2}>

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Affiliate Sourcess</Typography>

        <Button
          variant="contained"
          size="small"
          startIcon={<Plus />}
          onClick={() => { resetForm(); setOpen(true); }}
        >
          Add Sources
        </Button>
      </Box>

      {/* SEARCH */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search name / mobile / source"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* TABLE */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sr</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                  <TableRow key={row.id}>
                    <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{row.platform}</TableCell>
                    <TableCell>{row.sourceName}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.mobile}</TableCell>
                    <TableCell>{row.address}</TableCell>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>{row.status}</TableCell>

                    <TableCell>
                      <IconButton onClick={() => {
                        setFormData({
                          id: row.id,
                          platform: row.platform || "",
                          sourceName: row.sourceName || "",
                          code: row.code || "",
                          name: row.name || "",
                          mobile: row.mobile || "",
                          address: row.address || "",
                          status: row.status || "Active"
                        });
                        setOpen(true);
                      }}>
                        <Edit3 size={16} />
                      </IconButton>

                      <IconButton onClick={() => handleDelete(row.id)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </TableCell>

                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{formData.id ? "Edit Sources" : "Add Sources"}</DialogTitle>

        <DialogContent>
          <Grid className="row" spacing={2} mt={1}>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                select
                label="Platform"
                size="small"
                fullWidth
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select</option>
                <option value="Social Media">Social Media</option>
                <option value="Website">Website</option>
                <option value="Landing Page">Landing Page</option>
              </TextField>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                label="Source Name (Instagram / Website)"
                size="small"
                fullWidth
                value={formData.sourceName}
                onChange={(e) => setFormData({ ...formData, sourceName: e.target.value })}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                label="Customer Name"
                size="small"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                label="Mobile"
                size="small"
                fullWidth
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                label="Address"
                size="small"
                fullWidth
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                label="Code"
                size="small"
                fullWidth
                value={formData.code}
                disabled
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                select
                label="Status"
                size="small"
                fullWidth
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </TextField>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default Affiliatesources;